import { useState } from "react";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

// Blog post data
const blogPosts = [
  {
    id: 1,
    slug: "ayra-ai-vs-chat-dash",
    title: "Ayra AI vs Chat-Dash: A Complete Platform Comparison",
    excerpt: "An in-depth analysis of how Ayra AI's unified platform approach compares to Chat-Dash's modular toolkit for AI agencies.",
    category: "Comparisons",
    date: "Dec 15, 2024",
    featured: true,
    image: "https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80",
  },
  {
    id: 2,
    slug: "ayra-ai-vs-conduit",
    title: "Ayra AI vs Conduit: Which Platform Scales Better?",
    excerpt: "Comparing infrastructure, pricing models, and scalability between two leading AI agency platforms.",
    category: "Comparisons",
    date: "Dec 12, 2024",
    featured: false,
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80",
  },
  {
    id: 3,
    slug: "future-of-voice-ai",
    title: "The Future of Voice AI in Business Operations",
    excerpt: "How conversational AI is reshaping customer interactions and what agencies need to know to stay ahead.",
    category: "Voice AI",
    date: "Dec 10, 2024",
    featured: false,
    image: "https://images.unsplash.com/photo-1589254065878-42c9da997008?w=800&q=80",
  },
  {
    id: 4,
    slug: "icon-pest-control-case-study",
    title: "How Ayra AI Helped Icon Pest Control Automate 80% of Calls",
    excerpt: "A deep dive into how one agency transformed their client's operations with voice AI automation.",
    category: "Case Studies",
    date: "Dec 8, 2024",
    featured: false,
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?w=800&q=80",
  },
  {
    id: 5,
    slug: "building-ai-agency-2025",
    title: "Building an AI Agency in 2025: What Actually Works",
    excerpt: "Lessons from agencies that scaled past $100k MRR using platform-first approaches.",
    category: "Platform",
    date: "Dec 5, 2024",
    featured: false,
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80",
  },
  {
    id: 6,
    slug: "white-label-ai-guide",
    title: "The Complete Guide to White-Label AI Solutions",
    excerpt: "Everything you need to know about delivering AI under your own brand.",
    category: "Platform",
    date: "Dec 3, 2024",
    featured: false,
    image: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800&q=80",
  },
];

const categories = ["All", "Comparisons", "Voice AI", "Case Studies", "Platform"];

const Blog = () => {
  const [activeCategory, setActiveCategory] = useState("All");

  const featuredPost = blogPosts.find((post) => post.featured);
  const filteredPosts = blogPosts.filter((post) => {
    if (activeCategory === "All") return !post.featured;
    return post.category === activeCategory && !post.featured;
  });

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-4 md:px-6">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-center"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#5F8F84] mb-4 block">
              Ayra Journal
            </span>
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-semibold text-foreground tracking-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            >
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Insights on{" "}
              </motion.span>
              <motion.span 
                className="ayra-accent"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                whileHover={{ scale: 1.05 }}
              >
                AI
              </motion.span>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                , platforms,
              </motion.span>
              <br className="hidden md:block" />
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                and building real software businesses.
              </motion.span>
            </motion.h1>
            <motion.p 
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
              Thought leadership from the team building the future of AI agencies.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Featured Article */}
      {featuredPost && (
        <section className="pb-16 md:pb-24 px-4 md:px-6">
          <div className="container mx-auto max-w-6xl">
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
              className="group cursor-pointer"
            >
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                <div className="relative overflow-hidden rounded-2xl bg-muted aspect-[4/3]">
                  <img
                    src={featuredPost.image}
                    alt={featuredPost.title}
                    className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className="flex flex-col justify-center">
                  <motion.span 
                    className="text-sm font-medium text-[#5F8F84] mb-3"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    {featuredPost.category}
                  </motion.span>
                  <motion.h2 
                    className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground tracking-tight mb-4 group-hover:text-[#5F8F84] transition-colors duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    {featuredPost.title}
                  </motion.h2>
                  <motion.p 
                    className="text-muted-foreground text-base md:text-lg leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {featuredPost.excerpt}
                  </motion.p>
                </div>
              </div>
            </motion.article>
          </div>
        </section>
      )}

      {/* Category Filters */}
      <section className="pb-12 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap gap-2 md:gap-3"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                  activeCategory === category
                    ? "bg-foreground text-background"
                    : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="pb-24 md:pb-32 px-4 md:px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{
                  duration: 0.5,
                  delay: 0.1 * index,
                  ease: "easeOut",
                }}
                className="group cursor-pointer"
              >
                <div className="relative overflow-hidden rounded-xl bg-muted aspect-[4/3] mb-5">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-all duration-500 ease-out group-hover:scale-[1.03]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div>
                  <motion.span 
                    className="text-xs font-medium text-[#5F8F84] tracking-wide uppercase mb-2 block"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.1 * index + 0.1 }}
                  >
                    {post.category}
                  </motion.span>
                  <motion.h3 
                    className="text-lg md:text-xl font-semibold text-foreground tracking-tight mb-2 group-hover:text-[#5F8F84] transition-colors duration-300 line-clamp-2"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index + 0.2, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    {post.title}
                  </motion.h3>
                  <motion.p 
                    className="text-muted-foreground text-sm line-clamp-2 leading-relaxed"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 * index + 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {post.excerpt}
                  </motion.p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 md:py-28 px-4 md:px-6 bg-muted/30">
        <div className="container mx-auto max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-foreground tracking-tight mb-4">
              Stay ahead of the <span className="ayra-accent">curve</span>.
            </h2>
            <p className="text-muted-foreground mb-8">
              Get insights on AI, platforms, and agency growth delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#5F8F84]/20 focus:border-[#5F8F84] transition-all"
              />
              <button className="px-6 py-3 bg-foreground text-background font-medium rounded-lg hover:bg-foreground/90 transition-colors">
                Subscribe
              </button>
            </div>
            <p className="text-xs text-muted-foreground/60 mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
